const viteEnv = (import.meta as unknown as { env?: { DEV?: boolean; VITE_BACKEND_API_BASE?: string } }).env;
const API_BASE = viteEnv?.VITE_BACKEND_API_BASE || "/api";

export interface ApiResult<T> {
  data: T | null;
  error: string | null;
}

function stringifyApiError(detail: unknown, fallback: string): string {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object" && "msg" in item) {
          return String((item as { msg?: unknown }).msg);
        }
        return JSON.stringify(item);
      })
      .join("; ");
  }
  if (typeof detail === "object") {
    if ("message" in detail) return String((detail as { message?: unknown }).message);
    return JSON.stringify(detail);
  }
  return String(detail);
}

async function getSessionToken(): Promise<string | null> {
  const shopify = (window as unknown as { shopify?: { idToken?: () => Promise<string> } }).shopify;
  if (!shopify?.idToken) {
    return null;
  }
  try {
    return await shopify.idToken();
  } catch {
    return null;
  }
}

let installationPromise: Promise<void> | null = null;

function installationHttpError(response: Response, detail: unknown): string {
  const status = `HTTP ${response.status}${
    response.statusText ? ` ${response.statusText}` : ""
  }`;
  if (detail) {
    return stringifyApiError(
      detail,
      `Shopify managed installation could not be completed (${status})`
    );
  }
  if (response.status >= 500) {
    return `PrintConnect service is unavailable (${status}). Try again shortly.`;
  }
  return `Shopify managed installation could not be completed (${status})`;
}

export function ensureShopifyInstallation(): Promise<void> {
  if (installationPromise) return installationPromise;
  const attempt = (async () => {
    const token = await getSessionToken();
    if (!token) return;
    const response = await fetch(`${API_BASE}/shopify/auth/token-exchange`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`
      }
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      throw new Error(installationHttpError(response, payload?.detail || payload?.error));
    }
  })();
  installationPromise = attempt.catch((error) => {
    installationPromise = null;
    throw error;
  });
  return installationPromise;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<ApiResult<T>> {
  if (path !== "/shopify/auth/token-exchange") {
    try {
      await ensureShopifyInstallation();
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : "Shopify managed installation failed"
      };
    }
  }
  const token = await getSessionToken();
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  } else if (viteEnv?.DEV) {
    headers.set("X-PrintBridge-Dev-Auth", "true");
  }

  try {
    const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      const detail = payload?.detail || payload?.error || response.statusText;
      return { data: null, error: stringifyApiError(detail, response.statusText) };
    }
    if (payload === null) {
      return { data: null, error: "Backend returned an empty or non-JSON response" };
    }
    return { data: payload as T, error: null };
  } catch (error) {
    return { data: null, error: error instanceof Error ? error.message : "Network error" };
  }
}

export interface SetupStatus {
  backend: "ready" | "error";
  shopify_session: "verified" | "not_verified";
  shopify_admin_api?: "connected" | "missing" | "invalid";
  supplier_credentials: "configured" | "missing";
  app_proxy: "configured" | "missing";
  webhook_secret: "configured" | "missing";
  embedded_app_url: string | null;
  distribution_mode?: "custom" | "public" | string;
  billing_mode?: "free" | "shopify_billing" | string;
  billing?: BillingStatus;
  protected_customer_data?: "acknowledged" | "needs_acknowledgement" | string;
  config?: SetupConfig;
}

export interface BillingStatus {
  billing_mode: string;
  required: boolean;
  status: string;
  active: boolean;
  subscription?: Record<string, unknown> | null;
  plan?: {
    name: string;
    amount: number;
    currency: string;
    trial_days: number;
  };
}

export interface SetupConfig {
  shopify_api_key?: string;
  shopify_api_secret?: string;
  shopify_webhook_secret?: string;
  shopify_store_domain?: string;
  shopify_embedded_app_url?: string;
  shopify_distribution_mode?: "custom" | "public" | string;
  shopify_billing_mode?: "free" | "shopify_billing" | string;
  shopify_billing_plan_name?: string;
  shopify_billing_amount?: number;
  shopify_billing_currency?: string;
  shopify_billing_trial_days?: number;
  shopify_privacy_policy_url?: string;
  shopify_terms_url?: string;
  shopify_support_url?: string;
  support_email?: string;
  protected_customer_data_acknowledged?: boolean;
  printconnect_theme_template_name?: string;
  tradeprint_api_key?: string;
  tradeprint_username?: string;
  tradeprint_password?: string;
  tradeprint_api_prefix?: string;
  tradeprint_environment?: "sandbox" | "sandbox_docs" | "production";
  tradeprint_order_submission_enabled?: boolean;
  tradeprint_require_launch_certification?: boolean;
  enable_tradeprint?: boolean;
  enable_impact_signs?: boolean;
  enable_pencarrie?: boolean;
  experimental_suppliers_enabled?: boolean;
  pencarrie_api_token?: string;
  pencarrie_products_url?: string;
  pencarrie_stock_url?: string;
  pencarrie_timeout_seconds?: number;
}

export interface AdminContext {
  organization: {
    id: string;
    name: string;
    billing_mode: string;
    billing_status: string;
  };
  shop: StoreSummary;
  suppliers: SupplierMetadata[];
}

export interface StoreSummary {
  id: string;
  organization_id: string;
  shop_domain: string;
  store_key: string;
  install_status: string;
  app_proxy_status: string;
  theme_extension_status: string;
  billing_status: string;
  protected_customer_data_status: string;
  updated_at?: string | null;
}

export interface SupplierMetadata {
  key: string;
  display_name: string;
  status: string;
  is_beta: boolean;
  capabilities: Record<string, boolean>;
  setup_fields: string[];
  product_model: string;
}

export interface SupplierConnection {
  id: number;
  store_key: string;
  supplier_key: string;
  display_name: string;
  status: string;
  environment: string;
  is_enabled: boolean;
  is_beta: boolean;
  capabilities: Record<string, boolean>;
  public_config: Record<string, unknown>;
  metadata: SupplierMetadata;
  last_tested_at?: string | null;
  last_successful_sync_at?: string | null;
  last_error?: string | null;
}

export interface SupplierCatalogProduct {
  id: number;
  store_key: string;
  supplier_key: string;
  supplier_product_id: string;
  title: string;
  status: string;
  normalized_payload: Record<string, unknown>;
  variants?: Array<{
    supplier_variant_id: string;
    sku?: string | null;
    title: string;
    option_values: Record<string, unknown>;
    price?: string | null;
    stock_quantity?: number | null;
  }>;
  feed_hash?: string | null;
  last_seen_at?: string | null;
}

export interface ProductOptionRule {
  attribute?: string;
  name?: string;
  label?: string;
  id?: string;
  values?: unknown[];
  hidden_values?: unknown[];
  tooltip?: string | null;
  required?: boolean;
  [key: string]: unknown;
}

export interface ProductParityConfig extends Record<string, unknown> {
  rules?: ProductOptionRule[];
  delivery?: {
    enabled_service_levels?: string[];
    [key: string]: unknown;
  };
}

export interface AdminProduct {
  id?: string;
  name?: string;
  title?: string;
  supplier_key?: string;
  supplier_product_id?: string;
  catalog_source?: "supplier_api" | "imported_selection" | string;
  normalized_payload?: Record<string, unknown>;
  catalog_validation?: CatalogValidation;
  selection?: {
    product_id: string;
    product_group_id?: string | null;
    product_group_title?: string | null;
    product_group_role?: string | null;
    canonical_handle?: string | null;
    included: boolean;
    margin_percent: number;
    category?: string | null;
    collections?: string[];
    product_type?: string;
    variant_margins?: Record<string, number>;
    sync_status?: string | null;
    validation_issues?: string[];
    parity_config?: ProductParityConfig;
  };
}

export interface ConfigurationTemplate {
  id: string;
  name: string;
  description?: string | null;
  product_id: string;
  product_name?: string | null;
  product_path?: string | null;
  supplier_key?: "tradeprint" | "pencarrie" | "impactconnect" | null;
  configurator_mode?: "dynamic_print" | "impact_sign" | "simple" | "simple_print" | null;
  production_data: Record<string, string>;
  add_ons: string[];
  quantity: number;
  service_level: "Saver" | "Standard" | "Express" | "Same Day";
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ConfigurationTemplatePreview {
  valid: boolean;
  product_id: string;
  production_data: Record<string, string>;
  add_ons: string[];
  quantity: number;
  service_level: string;
  sell_price: string;
  currency?: string | null;
}

export interface ShopifyCollection {
  id: string;
  title: string;
  handle: string;
}

export interface ArtworkPolicy {
  required: boolean;
  allow_upload_later: boolean;
  artwork_service: "Just Print" | "File Check" | "File Assist" | "File Check with Proofing" | string;
  allowed_mime_types: string[];
  max_file_size_mb: number;
  proof_required: boolean;
}

export interface CatalogValidationIssue {
  code: string;
  message: string;
  severity: string;
  category: string;
}

export interface CatalogValidation {
  score: number;
  status: "ready" | "needs_review" | "blocked" | string;
  issues: CatalogValidationIssue[];
  warnings: CatalogValidationIssue[];
  summary: string[];
  signals: Record<string, unknown>;
}

export interface ProductLaunchCertification {
  id: number;
  supplier_key: string;
  supplier_product_id: string;
  product_name?: string | null;
  attributes_verified: boolean;
  quantities_verified: boolean;
  prices_verified: boolean;
  preflight_verified: boolean;
  sandbox_order_verified: boolean;
  webhook_lifecycle_verified: boolean;
  simulator_verified: boolean;
  missing: string[];
  missing_evidence: string[];
  status: string;
  evidence: Record<string, unknown>;
  last_checked_at?: string | null;
  updated_at?: string | null;
}

export interface TradeprintWebhookEvent {
  id: number;
  webhook_notification_id: string;
  order_reference?: string | null;
  item_reference?: string | null;
  shopify_order_id?: string | null;
  status?: string | null;
  processed_status: string;
  received_at?: string | null;
  processed_at?: string | null;
}

export interface LaunchReadinessResponse {
  ready: boolean;
  blockers: string[];
  certifications: {
    total: number;
    certified: number;
    not_ready: number;
    items: ProductLaunchCertification[];
  };
  recent_tradeprint_webhooks: TradeprintWebhookEvent[];
  manual_review_count: number;
  production_gates: {
    tradeprint_require_launch_certification: boolean;
    tradeprint_order_submission_enabled: boolean;
  };
}

export interface PriceListRow {
  product_name: string;
  product_id?: string | null;
  source_format?: string | null;
  payload?: Record<string, unknown> | null;
  row_count?: number;
  imported_at?: string | null;
  checksum?: string | null;
}

export interface SyncJob {
  id: string;
  job_type: string;
  status: string;
  checkpoint?: Record<string, unknown> | null;
  failure_summary?: {
    failed_count?: number;
    first_failed_item?: string | null;
    first_error_code?: string | null;
    first_error_detail?: string | null;
    items?: Array<{
      item_key?: string | null;
      error_code?: string | null;
      error_detail?: string | null;
      updated_at?: string | null;
    }>;
  };
  updated_at?: string;
}

export interface SyncJobsResponse {
  jobs: SyncJob[];
  facets: {
    statuses: string[];
    job_types: string[];
  };
  pagination: {
    offset: number;
    limit: number;
    returned: number;
    total: number;
    has_previous: boolean;
    has_next: boolean;
  };
}

export interface CatalogDriftEvent {
  id: number;
  product_name: string;
  event_type: string;
  severity: "high" | "warning" | "medium" | string;
  status: "open" | "resolved" | "dismissed" | string;
  resolution_note?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  created_at?: string | null;
  occurrence_count: number;
  last_seen_at?: string | null;
}

export interface CatalogDriftResponse {
  events: CatalogDriftEvent[];
  summary: {
    total: number;
    occurrences: number;
    action_required: number;
    by_severity: Record<string, number>;
    by_type: Record<string, number>;
  };
}

export interface ReviewItem {
  review_key: string;
  status: string;
  severity: string;
  reason_code: string;
  order_id?: string | null;
  context?: Record<string, unknown>;
  created_at?: string;
  updated_at?: string;
}

export interface ReviewQueueResponse {
  items: ReviewItem[];
  summary: {
    open: number;
    critical: number;
    high: number;
    by_severity: Record<string, number>;
  };
  facets: {
    severities: string[];
    reasons: string[];
    statuses: string[];
  };
  pagination: {
    offset: number;
    limit: number;
    returned: number;
    total: number;
    has_previous: boolean;
    has_next: boolean;
  };
}

export interface OrderLifecycleItem {
  shopify_order_id?: string | null;
  tradeprint_order_id?: string | null;
  status: string;
  action_required: boolean;
  decision_action?: string | null;
  decision_reason?: string | null;
  pricing_status?: string | null;
  shopify_fulfillment_status?: string | null;
  shopify_fulfillment_request_status?: string | null;
  shopify_fulfillment_request_already_submitted?: boolean;
  can_request_shopify_fulfillment?: boolean;
  proof_status?: string | null;
  proof_version?: string | null;
  proof_expires_at?: string | null;
  can_publish_proof?: boolean;
  merchant_hold_active?: boolean;
  merchant_hold_reason?: string | null;
  merchant_hold_at?: string | null;
  can_place_hold?: boolean;
  can_release_hold?: boolean;
  customer_update_message?: string | null;
  customer_update_published_at?: string | null;
  local_fulfillment_status?: string | null;
  local_fulfillment_item_count?: number | null;
  local_fulfillment_suppliers?: string[];
  local_fulfillment_customer_message?: string | null;
  tracking_number?: string | null;
  last_error?: string | null;
  last_synced_at?: string | null;
  updated_at?: string | null;
}

export interface OrderLifecycleResponse {
  items: OrderLifecycleItem[];
  summary: {
    total: number;
    action_required: number;
    by_status: Record<string, number>;
  };
  facets?: {
    statuses: string[];
    routes: string[];
  };
  pagination?: {
    offset: number;
    limit: number;
    returned: number;
    total: number;
    has_previous: boolean;
    has_next: boolean;
  };
}

export interface SupplierControls {
  supplier_key: string;
  enabled: boolean;
  submit_only_manual: boolean;
  degraded: boolean;
  reason?: string | null;
  source_rules?: Array<Record<string, unknown>>;
}

export interface FulfillmentRulesResponse {
  rules: Array<Record<string, unknown>>;
  tradeprint_controls: SupplierControls;
  config_submission_enabled: boolean;
}

export interface Kpis {
  counts?: Record<string, number>;
  submission_success_rate?: number;
  submission_failure_rate?: number;
  reconciliation_mismatch_count?: number;
  manual_review_count?: number;
  stale_order_count?: number;
  stale_orders?: Array<Record<string, unknown>>;
  fulfillment_writeback_pending_count?: number;
  fulfillment_writeback_pending?: Array<Record<string, unknown>>;
  operational_health_score?: number;
  health_thresholds?: Record<string, unknown>;
  artwork_funnel?: Record<string, number>;
  commercial?: CommercialKpis;
  [key: string]: unknown;
}

export interface CommercialKpis {
  currency?: string | null;
  currency_status?: "single" | "mixed" | "unknown";
  currencies?: string[];
  tracked_revenue?: string | null;
  covered_revenue?: string | null;
  verified_supplier_cost?: string | null;
  covered_gross_profit?: string | null;
  covered_gross_margin_percent?: string | null;
  orders_considered?: number;
  orders_with_revenue?: number;
  orders_missing_revenue?: number;
  orders_with_verified_cost?: number;
  orders_missing_verified_cost?: number;
  cost_coverage_percent?: number;
  excluded_orders?: Record<string, number>;
}

export interface AnalyticsFunnelDay {
  date: string;
  [event: string]: string | number;
}

export interface AnalyticsProductPerformance {
  product_id: string;
  product_name: string;
  views: number;
  quotes: number;
  baskets: number;
  checkouts: number;
  paid_orders: number;
  quote_rate: number;
  basket_rate: number;
}

export interface AnalyticsFunnelResponse {
  window_days: 7 | 30 | 90 | number;
  generated_at: string;
  total_events: number;
  unique_journeys: number;
  counts: Record<string, number>;
  rates: Record<string, number>;
  by_day: AnalyticsFunnelDay[];
  product_performance: AnalyticsProductPerformance[];
  product_count: number;
}

export interface RetentionStatus {
  store_key: string;
  dry_run: boolean;
  generated_at: string;
  policy: Record<string, number>;
  cutoffs: Record<string, string>;
  counts: Record<string, number>;
  total: number;
}

export interface QueueHealth {
  queue: string;
  status: string;
  count?: number;
  failed_job_registry_count?: number;
  started_job_registry_count?: number;
  deferred_job_registry_count?: number;
  error?: string;
}

export interface OperationsRisk {
  id: string;
  category: "artwork" | "jobs" | "orders" | "pricing" | "supplier" | string;
  severity: "critical" | "high" | "warning" | string;
  title: string;
  detail: string;
  count: number;
  action_label: string;
  action_path: string;
}

export interface OperationsHealth {
  generated_at: string;
  store_key: string;
  status: "healthy" | "warning" | "critical" | string;
  score: number;
  recommendations: string[];
  supplier_controls: {
    tradeprint: SupplierControls;
  };
  suppliers: SupplierConnection[];
  queues: QueueHealth[];
  recent_jobs: SyncJob[];
  manual_reviews: ReviewItem[];
  risks: OperationsRisk[];
  risk_summary: {
    action_required: number;
    items: number;
    by_severity: Record<string, number>;
    by_category: Record<string, number>;
    margin_threshold_percent: number;
    quote_recovery_warning_count: number;
  };
  kpis: Kpis;
  retention: RetentionStatus;
  audit_events: AuditEvent[];
  alerting: {
    configured: boolean;
    destination: "platform_dashboard";
    external_webhook_configured: boolean;
  };
}

export interface AuditEvent {
  id: number;
  actor_id?: string | null;
  actor_role?: string | null;
  action: string;
  entity_type: string;
  entity_id: string;
  payload?: Record<string, unknown>;
  created_at?: string | null;
}

export interface AuditResponse {
  items: AuditEvent[];
  facets: {
    actions: string[];
    entity_types: string[];
    actors: string[];
  };
  pagination: {
    offset: number;
    limit: number;
    returned: number;
    total: number;
    has_previous: boolean;
    has_next: boolean;
  };
}

export interface ChannelReadinessSummary {
  key: string;
  label: string;
  description: string;
  ready: number;
  needs_review: number;
  blocked: number;
  total: number;
  status: string;
}

export interface ChannelProductReadiness {
  product_id: string;
  title: string;
  supplier_key: string;
  included: boolean;
  status: string;
  ready_channel_count: number;
  total_channel_count: number;
  primary_gaps: string[];
  signals: {
    catalog_score?: number;
    catalog_status?: string;
    option_count: number;
    option_value_count: number;
    service_levels: string[];
    margin_percent: number;
    taxable?: boolean | null;
    has_media_policy: boolean;
  };
  channels: Record<string, { status: string; gaps: string[] }>;
}

export interface ChannelRecommendation {
  title: string;
  detail: string;
}

export interface ChannelReadinessResponse {
  channels: Array<{ key: string; label: string; description: string }>;
  summary: ChannelReadinessSummary[];
  products: ChannelProductReadiness[];
  recommendations: ChannelRecommendation[];
}

export interface ChannelFeedPreview {
  channel: { key: string; label: string; description: string };
  eligible_count: number;
  blocked_count: number;
  items: Array<Record<string, unknown>>;
  blocked: Array<{
    product_id: string;
    title: string;
    status: string;
    gaps: string[];
    preview_item: Record<string, unknown>;
  }>;
  fields: string[];
}

export type ChannelExportFormat = "xml" | "csv";

export interface ChannelFeedExport {
  channel: string;
  format: ChannelExportFormat;
  filename: string;
  mime_type: string;
  content: string;
  eligible_count: number;
  excluded_count: number;
  warnings: string[];
  generated_at: string;
}
